import { router } from '@inertiajs/react';
import { RefObject, useEffect, useRef } from 'react';

const EMBED_MESSAGE_TYPE = 'diagonal-embed-resize';
const STABILIZATION_INTERVAL = 50;
const MAX_STABILIZATION_CHECKS = 20;

export function useEmbedHeightReporter(
  pageId?: string,
  contentRef?: RefObject<HTMLElement | null>,
  // Auto-height reporting serves the public embed script, which resizes the
  // iframe to fit; surfaces framed at a fixed height (the editor's draft
  // preview) opt out so the body keeps its natural sizing.
  { isEnabled = true }: { isEnabled?: boolean } = {}
) {
  const lastHeightRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const isInIframe = window.self !== window.top;
    if (!isInIframe) {
      return;
    }

    document.body.style.height = 'auto';
    document.body.style.minHeight = '0';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.minHeight = '0';

    const contentElement =
      contentRef?.current?.closest<HTMLElement>('[data-page-embed-content]') ?? contentRef?.current;

    function getHeight(): number {
      if (contentElement) {
        const rect = contentElement.getBoundingClientRect();
        const style = window.getComputedStyle(contentElement);
        const marginTop = parseFloat(style.marginTop) || 0;
        const marginBottom = parseFloat(style.marginBottom) || 0;
        return Math.ceil(rect.height + marginTop + marginBottom);
      }
      return document.body.scrollHeight;
    }

    function reportHeight() {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const height = getHeight();

        if (height !== lastHeightRef.current) {
          lastHeightRef.current = height;
          window.parent.postMessage({ type: EMBED_MESSAGE_TYPE, height }, '*');
        }
      });
    }

    function clearStabilizationInterval() {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }

    function startStabilizationChecks() {
      clearStabilizationInterval();

      let checks = 0;
      let stableCount = 0;
      let lastHeight = 0;

      intervalIdRef.current = setInterval(() => {
        checks = checks + 1;
        const height = getHeight();

        if (height !== lastHeightRef.current) {
          reportHeight();
          stableCount = 0;
        } else if (height === lastHeight) {
          stableCount = stableCount + 1;
        } else {
          stableCount = 0;
        }

        lastHeight = height;

        if (stableCount >= 3 || checks >= MAX_STABILIZATION_CHECKS) {
          clearStabilizationInterval();
        }
      }, STABILIZATION_INTERVAL);
    }

    function setupImageListeners() {
      document.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', reportHeight, { once: true });
          img.addEventListener('error', reportHeight, { once: true });
        }
      });
    }

    lastHeightRef.current = 0;
    reportHeight();

    requestAnimationFrame(() => {
      setupImageListeners();
      reportHeight();
      startStabilizationChecks();
    });

    const resizeObserver = new ResizeObserver(reportHeight);
    resizeObserver.observe(document.body);
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      reportHeight();

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLImageElement && !node.complete) {
            node.addEventListener('load', reportHeight, { once: true });
            node.addEventListener('error', reportHeight, { once: true });
          } else if (node instanceof HTMLElement) {
            node.querySelectorAll('img').forEach((img) => {
              if (!img.complete) {
                img.addEventListener('load', reportHeight, { once: true });
                img.addEventListener('error', reportHeight, { once: true });
              }
            });
          }
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    const removeNavigateListener = router.on('navigate', () => {
      lastHeightRef.current = 0;
      reportHeight();

      requestAnimationFrame(() => {
        setupImageListeners();
        reportHeight();
        startStabilizationChecks();
      });
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      removeNavigateListener();
      clearStabilizationInterval();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [pageId, contentRef, isEnabled]);
}
